/**
 * DetailedFunnelPanel — the "Phase 1 : Comprendre" view that shows the
 * enriched product funnel (signup → dashboard → map opened → zone clicked
 * → mission started → mission saved → paid) so we can see EXACTLY which
 * step is losing users.
 *
 * Data comes from `GET /api/admin/analytics/detailed-funnel` which reads
 * from the `product_events` collection populated by the frontend tracker
 * at `/app/frontend/src/lib/analytics.js`.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Activity, ArrowDownRight, RotateCw, AlertOctagon } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERIODS = [
  { value: 7,   label: '7 derniers jours' },
  { value: 30,  label: '30 derniers jours' },
  { value: 90,  label: '90 derniers jours' },
];

const dateFmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso.slice(0, 16); }
};

const EVENT_LABEL = {
  page_view: 'Page vue',
  dashboard_viewed: 'Dashboard',
  map_opened: 'Carte ouverte',
  map_search_performed: 'Recherche carte',
  map_zone_clicked: 'Zone cliquée',
  drone_form_opened: 'Form drone',
  drone_added: 'Drone ajouté',
  mission_started: 'Mission démarrée',
  mission_saved: 'Mission enregistrée',
  pricing_viewed: 'Pricing vu',
  pricing_cta_clicked: 'CTA pricing',
  checkout_started: 'Checkout démarré',
  checkout_completed: 'Payé',
  pdf_exported: 'PDF exporté',
};

export function DetailedFunnelPanel({ token }) {
  const [period, setPeriod] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/analytics/detailed-funnel`, {
        params: { period_days: period, limit: 100 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch {
      toast.error('Erreur chargement funnel produit');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [period]);

  if (!data) return <div className="text-slate-500">Chargement…</div>;

  const maxCount = Math.max(...(data.funnel || []).map((s) => s.count), 1);

  return (
    <div className="space-y-6" data-testid="detailed-funnel-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Activity className="w-5 h-5 text-emerald-600" />
          <h2 className="text-lg font-bold text-slate-900 font-[Barlow]">Parcours produit — où ils partent</h2>
          <select
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
            className="h-9 px-3 rounded-md border border-slate-300 bg-white text-sm"
            data-testid="detailed-funnel-period"
          >
            {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1 px-3 h-9 rounded-md text-sm border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50"
            data-testid="detailed-funnel-refresh"
          >
            <RotateCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Rafraîchir
          </button>
        </div>
        <div className="text-xs text-slate-500">
          Cohorte : <strong>{data.cohort_size}</strong> inscrits sur la période
        </div>
      </div>

      {/* Biggest drop-off callout */}
      {data.biggest_drop && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex items-start gap-3" data-testid="biggest-drop-callout">
          <AlertOctagon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-bold text-amber-900">
              Plus grosse perte : {data.biggest_drop.step_from} → {data.biggest_drop.step_to}
            </div>
            <div className="text-sm text-amber-800 mt-0.5">
              Seulement <strong>{data.biggest_drop.vs_prev_pct}%</strong> franchissent cette étape — {data.biggest_drop.lost_users} utilisateurs perdus. C'est là qu'il faut agir en priorité.
            </div>
          </div>
        </div>
      )}

      {/* Enriched funnel bars */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="space-y-2.5">
          {data.funnel.map((step, idx) => {
            const width = step.count ? Math.max(6, (step.count / maxCount) * 100) : 4;
            const isBigDrop = data.biggest_drop && data.biggest_drop.key_to === step.key;
            return (
              <div key={step.key} className="flex items-center gap-3">
                <div className="w-52 text-sm font-medium text-slate-700">
                  {step.step}
                </div>
                <div className={`flex-1 rounded-md h-8 relative overflow-hidden ${isBigDrop ? 'bg-amber-50' : 'bg-slate-100'}`}>
                  <div
                    className={`h-8 rounded-md transition-all ${isBigDrop ? 'bg-gradient-to-r from-amber-500 to-amber-400' : 'bg-gradient-to-r from-emerald-500 to-emerald-400'}`}
                    style={{ width: `${width}%` }}
                  />
                  <div className="absolute inset-0 flex items-center px-3 text-sm font-semibold text-slate-900">
                    {step.count} <span className="ml-2 text-slate-500 font-normal">({step.pct}%)</span>
                  </div>
                </div>
                <div className="w-24 text-right text-xs">
                  {idx > 0 && step.vs_prev_pct != null ? (
                    <span className={step.vs_prev_pct < 50 ? 'text-rose-600 font-semibold' : 'text-slate-500'}>
                      <ArrowDownRight className="w-3 h-3 inline mr-0.5" />
                      {step.vs_prev_pct}%
                    </span>
                  ) : <span className="text-slate-300">—</span>}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 pt-3 border-t border-slate-100 text-xs text-slate-500">
          La colonne de droite indique le taux de conversion depuis l'étape précédente. En rouge quand &lt; 50%.
        </div>
      </div>

      {/* Latest cohort — where each user is right now */}
      {data.recent_users_with_last_step?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="recent-cohort-table">
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
            <h3 className="text-sm font-bold text-slate-900 font-[Barlow]">Derniers inscrits — dernière action produit</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Repère les utilisateurs qui se sont inscrits mais ne sont jamais allés jusqu'à la carte, ou qui ont ouvert la carte mais n'ont pas exploré de zone.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="text-left px-6 py-2.5 font-semibold">Utilisateur</th>
                  <th className="text-left px-6 py-2.5 font-semibold">Pays</th>
                  <th className="text-left px-6 py-2.5 font-semibold">Inscrit</th>
                  <th className="text-left px-6 py-2.5 font-semibold">Dernière action</th>
                  <th className="text-left px-6 py-2.5 font-semibold">Quand</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.recent_users_with_last_step.slice(0, 30).map((u) => (
                  <tr key={u.id} className="hover:bg-slate-50">
                    <td className="px-6 py-2.5">
                      <div className="font-medium text-slate-900">{u.name || '—'}</div>
                      <div className="text-xs text-slate-500">{u.email}</div>
                    </td>
                    <td className="px-6 py-2.5 text-slate-700 uppercase text-xs">{u.country || '—'}</td>
                    <td className="px-6 py-2.5 text-slate-500 text-xs">{dateFmt(u.created_at)}</td>
                    <td className="px-6 py-2.5">
                      {u.last_event ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-xs font-medium">
                          {EVENT_LABEL[u.last_event] || u.last_event}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-rose-50 text-rose-700 text-xs font-medium">
                          Aucune activité produit
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-2.5 text-slate-500 text-xs">{dateFmt(u.last_event_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
